import { Ref, useEffect, useMemo, useState } from "react";
import { Map } from "@/components/atoms";
import { Coordinates, MapHandle } from "@/components/atoms/Map/Map";
import { useIsMdUp } from "@/hooks/useIsMdUp";
import { getRoutePath } from "@/lib/mapbox";
import { useAppSelector } from "@/redux/hooks";
import { selectOrderedLocations } from "@/redux/slices/clientBooking/selectors";

// The Address panel overlays the map's left side on md+ and the bottom on mobile; pad the
// fit-to-markers on that side so pickup/stops/destination never hide behind the panel.
const DESKTOP_FIT = { top: 64, right: 64, bottom: 64, left: 440 };
const MOBILE_FIT = { top: 64, right: 24, bottom: 360, left: 24 };

/**
 * Full-bleed map behind the booking panels: plots the booking's pickup/stops/destination as
 * purple pins and draws the route polyline between them. The pin picker is a separate
 * modal (PinLocationModal). Layout-only default (Sydney) until locations are set.
 */
export const BookingMapBackground = ({ mapControlRef }: { mapControlRef?: Ref<MapHandle> }) => {
  const allLocations = useAppSelector(selectOrderedLocations);
  /*
   * Only the locations we can plot. A booking whose address was never validated ("Requires
   * Validation") carries no lat/lng, and passing those through produced two failures at once: Google
   * threw InvalidValueError out of setCenter/setPosition — escaping to the error boundary and blanking
   * the sheet — and the directions call went to Mapbox as ".../driving-traffic/,;," which it rejects.
   *
   * Dropping them shows the trip without that leg, which is the honest picture: we do not know where
   * it is. The sheet itself still lists the address in the summary.
   */
  const ordered = useMemo(
    () => allLocations.filter((p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng)),
    [allLocations]
  );
  const [routePath, setRoutePath] = useState<Coordinates[]>([]);

  const isMdUp = useIsMdUp();
  const fitPadding = isMdUp ? DESKTOP_FIT : MOBILE_FIT;

  // Stable key so the directions effect only re-fires when the actual coordinates change.
  const routeKey = ordered.map((p) => `${p.lat},${p.lng}`).join("|");
  useEffect(() => {
    if (ordered.length < 2) {
      setRoutePath([]);
      return;
    }
    let cancelled = false;
    getRoutePath(ordered.map((p) => ({ lat: p.lat, lng: p.lng }))).then((path) => {
      if (!cancelled) setRoutePath(path);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  return (
    <div className="h-full w-full">
      <Map
        ref={mapControlRef}
        className="!rounded-none"
        zoom={13}
        fitToMarkers
        fitPadding={fitPadding}
        // Anchor center to the first booking location so a late geolocation fix can't recenter the
        // map away from the fitted markers/route; fitToMarkers still frames the whole trip.
        center={ordered.length ? { lat: ordered[0].lat, lng: ordered[0].lng } : undefined}
        markers={ordered.map((p, i) => ({ position: { lat: p.lat, lng: p.lng }, variant: i === 0 ? "dot" : "pin" }))}
        routePath={routePath}
      />
    </div>
  );
};
